import PropTypes from 'prop-types';
import React from 'react';
import styles from './Input.less';
import ExclamationMark from '../Svg/ExclamationMark';
import Tooltip from 'wix-style-react/Tooltip';

const ERROR_POSITIONS = {
    TOOLTIP: 'tooltip',
    BOTTOM: 'bottom'
};

export default class extends React.Component {
    static displayName = 'Input';

    static propTypes = {
        value: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        containerClass: PropTypes.string,
        inputClass: PropTypes.string,
        placeholder: PropTypes.string,
        placeholderClass: PropTypes.string,
        errorTooltipClass: PropTypes.string,
        focusClass: PropTypes.string,
        onChange: PropTypes.func,
        showErrorText: PropTypes.bool,
        error: PropTypes.string,
        errorTooltip: PropTypes.bool,
        errorPosition: PropTypes.string,
        focus: PropTypes.bool,
        autocomplete: PropTypes.string,
        onEnter: PropTypes.func,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        maxLength: PropTypes.number,
        dataHook: PropTypes.string
    };

    static defaultProps = {
        onChange: () => {},
        value: '',
        error: '',
        type: 'text',
        errorPosition: ERROR_POSITIONS.TOOLTIP,
        showErrorText: true,
        errorTooltip: true,
        autocomplete: 'on',
        onEnter: () => {},
        onFocus: () => {},
        onBlur: () => {},
        maxLength: 500
    };

    constructor(props) {
        super(props);
        this.uniqueId = `${Math.random()}`;

        this.state = {
            isHoverOnError: false,
            isFocused: false,
            isTouchDevice: ('ontouchstart' in window) || window.navigator && window.navigator.maxTouchPoints,
            isTouching: false,
            error: props.error
        };
    }

    componentDidMount() {
        const { isTouchDevice } = this.state;
        const { focus } = this.props;
        if (focus) {
            this._focus();
        }
        if (isTouchDevice) {
            this._addTouchEvent();
        }
    }

    componentWillUnmount() {
        const { isTouchDevice } = this.state;
        if (isTouchDevice) {
            this._removeTouchEvent();
        }
    }

    componentWillReceiveProps(nextProps) {
        if (!this.props.error !== nextProps.error) {
            this.setState({error: nextProps.error});
        }
    }

    render() {
        const { isFocused } = this.state;
        const { errorTooltip, errorPosition, showErrorText } = this.props;
        const className = this._getClassName();
        const error = this._getError();
        let content = this._getInputContent();

        if (errorPosition === ERROR_POSITIONS.BOTTOM && showErrorText) {
            content = [content, error];
        } else if (errorTooltip && errorPosition === ERROR_POSITIONS.TOOLTIP) {
            content = (
                <Tooltip key="input"
                         content={error || ''}
                         placement="left"
                         alignment="center"
                         theme="error"
                         showDelay={200}
                         hideDelay={0}
                         showTrigger="custom"
                         hideTrigger="custom"
                         active={errorTooltip && errorPosition === ERROR_POSITIONS.TOOLTIP && error && isFocused}
                         onActiveChange={() => {}}>
                    {content}
                </Tooltip>
            );
        }

        return (
            <div className={className} >
                {content}
            </div>
        );
    }

    focus = () => {
        if (this.input) {
            this.input.focus();
        }
    };

    _getInputContent = () => {
        const { type, placeholder, placeholderClass, onChange, value, inputClass, autocomplete, name, error, dataHook, maxLength } = this.props;
        const exclamation = this._getExclamation();
        const inputClassName = this._getInputClassName();

        return (
            <div className={`${styles.wrapper} ${inputClass} ${inputClassName}`} key="input" onClick={this._focus} >
                <label htmlFor={`input_${this.uniqueId}`}>{placeholder}</label>
                <div className={`${styles.placeholder} ${placeholderClass}`}><span id={`input-placeholder_${this.uniqueId}`}>{placeholder}</span></div>
                <input type={type} id={`input_${this.uniqueId}`}
                       name={name}
                       maxLength={maxLength}
                       value={value}
                       ref={el => this.input = el}
                       onChange={e => onChange(e.target.value)}
                       onKeyUp={this._onKeyUp}
                       onFocus={this._onFocus}
                       onBlur={this._inputBlur}
                       required="true"
                       aria-invalid={!!error}
                       aria-labelledby={`input-placeholder_${this.uniqueId}`}
                       aria-describedby={`input-error_${this.uniqueId}`}
                       data-hook={dataHook}
                       autoComplete={autocomplete}/>
                {exclamation}
            </div>
        );
    };

    _inputBlur = () => {
        const { onBlur } = this.props;
        const newState = {isFocused: false};

        if (!this.props.error) {
            newState.error = null;
        }

        this.setState(newState);
        onBlur();
    };

    _onFocus = () => {
        const { onFocus } = this.props;
        this.setState({isFocused: true});
        onFocus();
    };

    _getError = () => {
        const { error } = this.state;
        const { errorPosition } = this.props;
        let errorContent = error;

        if (error && errorPosition === ERROR_POSITIONS.BOTTOM) {
            errorContent = (
                <div key="error" className={styles.errorContainer}>{error}</div>
            );
        }

        return errorContent;
    };

    _focus = (e) => {
        const { isTouching } = this.state;
        if (isTouching) {
            if (e) {
                e.preventDefault();
            }
            return false;
        }
        this.input.focus();
    };

    _onKeyUp = (e) => {
        const { onEnter } = this.props;
        if (onEnter && e.which === 13) {
            onEnter();
        }
    };

    _getClassName = () => {
        const { error } = this.state;
        const { value, errorPosition, containerClass } = this.props;
        let cls = `${styles.outerWrapper} ${containerClass}`;

        if (value) {
            cls += ` ${styles.dirty}`;
        }
        if (error) {
            cls += ` input-error ${styles.error} ${styles[errorPosition] || ''}`;
        }

        return cls;
    };

    _getInputClassName = () => {
        const { isFocused } = this.state;
        const { focusClass } = this.props;
        let cls = '';

        if (isFocused && focusClass) {
            cls += focusClass;
        }

        return cls;
    };

    _getExclamation = () => {
        const { error } = this.state;

        return error ? (
                <div className={`${styles.exclamation}`} data-hook="error"><span role="alert" className={styles.hiddenError} id={`input-error_${this.uniqueId}`}>{error}</span><ExclamationMark /></div>
            ) : null;
    };

    _addTouchEvent = () => {
        $(document).on('touchstart', this._onTouchStart);
        $(document).on('touchend', this._onTouchEnd);
    };

    _removeTouchEvent = () => {
        $(document).off('touchstart', this._onTouchStart);
        $(document).off('touchend', this._onTouchEnd);
    };

    _onTouchStart = () => {
        clearTimeout(this.touchTimeout);
        this.setState({isTouching: true});
    };

    _onTouchEnd = () => {
        this.touchTimeout = setTimeout(() => {
            this.setState({isTouching: false});
        }, 200);
    };
}
